import type { ReactNode } from "react";

interface CenterProps {
  children: ReactNode;
  /**
   * Height of the centring area, as a CSS length. Without it the area is only
   * as tall as its content, so nothing moves — pass a value (e.g. a viewport
   * calc that subtracts your app's nav + footer chrome, `calc(100vh - 200px)`)
   * to centre the content in the visible region instead. The chrome figure is
   * app-specific, so it lives at the call site, not here.
   */
  minHeight?: string | number | undefined;
  /**
   * Cap the centred content's width (px). Handy for a standalone card whose
   * content (a sentence of copy) would otherwise stretch it wide.
   */
  maxWidth?: number | undefined;
}

/**
 * Centres its children horizontally and vertically within a region. Use it for
 * a single standalone card on an otherwise empty screen — a lookup form, a
 * "not found" panel — where the content should sit in the middle rather than at
 * the top. Give it a `minHeight` so there's a region to centre within.
 */
export function Center({ children, minHeight, maxWidth }: CenterProps) {
  return (
    <div
      className="flex w-full items-center justify-center"
      {...(minHeight !== undefined && { style: { minHeight } })}
    >
      <div
        className="w-full"
        {...(maxWidth !== undefined && { style: { maxWidth } })}
      >
        {children}
      </div>
    </div>
  );
}
